import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators'
import * as fastLevenshtein from 'fast-levenshtein'

import { ResourceDefinition } from '../../../../abacus/interfaces/resource-definition.interface'
import { SearchResult } from '../../../../abacus/interfaces/search-result.interface'
import { ResourceService } from '../../../../abacus/services/resource.service'

@Component({
  selector: 'app-ref-search-input',
  templateUrl: './ref-search-input.component.html',
  styleUrls: ['./ref-search-input.component.scss'],
  providers: [ResourceService],
  standalone: true,
  imports: [CommonModule]
})
export class RefSearchInputComponent implements OnChanges, OnInit, OnDestroy {
  @Input() label: string
  @Input() icon: string = 'icon-search'
  @Input() initialValue: number[]
  @Input() resources: ResourceDefinition[]
  @Input() maxResults: number = 0
  @Input() readonly = false
  @Input() resourceType: string = 'tool'

  @Output() valueChanged: EventEmitter<{ [key: string]: number[] }> = new EventEmitter()

  @ViewChild('searchInput', { static: false }) searchInputEl: ElementRef

  suggestedSearchResults: SearchResult[] = []
  selectedSearchResults: SearchResult[] = []

  private search$ = new Subject<string>()
  private destroy$ = new Subject<void>()
  terms = ''
  showList = false
  focusedItemIndex: number

  searching = false
  searchComplete = false

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private resourceService: ResourceService
  ) {}

  ngOnInit() {
    this.search$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((terms) =>
          this.resourceService.list('search', {
            resources: this.resources.map((rD) => rD.className),
            terms,
            statuses: ['Published']
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((searchResultsRes: SearchResult[]) => {
        this.suggestedSearchResults = (searchResultsRes as SearchResult[]).sort(
          (a: SearchResult, b: SearchResult) =>
            fastLevenshtein.get(this.terms, a.label) -
            fastLevenshtein.get(this.terms, b.label)
        )

        if (this.maxResults > 0) {
          this.suggestedSearchResults = this.suggestedSearchResults.slice(0, this.maxResults)
        }

        delete this.focusedItemIndex
        this.searching = false
        this.searchComplete = true
      })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  async ngOnChanges() {
    if (!this.initialValue?.length || !this.resources?.length) {
      this.selectedSearchResults = []
      return
    }

    const key = this.resources[0].className.charAt(0).toLowerCase() + this.resources[0].className.slice(1) + 'Ids'
    const result = await this.resourceService.list('search/get-search-result-objects', { [key]: this.initialValue, statuses: ['Published'] })
    this.selectedSearchResults = result as SearchResult[]
  }

  toggleItem(item: SearchResult): void {
    if (this.readonly) return

    const idx = this.selectedSearchResults.findIndex((i: any) => i.id === item.id)
    if (idx >= 0) {
      this.selectedSearchResults.splice(idx, 1)
    } else {
      this.selectedSearchResults.push(item)
    }

    this.terms = ''
    this.searching = false
    this.renderer.setProperty(this.searchInputEl.nativeElement, 'value', '')
    this.showList = false
    this.suggestedSearchResults = []
    this.valueChanged.emit(this.formatToEmit(this.selectedSearchResults))
  }

  removeItem(item: SearchResult): void {
    if (this.readonly) return

    this.selectedSearchResults = this.selectedSearchResults.filter((i: any) => i.id !== item.id)
    this.valueChanged.emit(this.formatToEmit(this.selectedSearchResults))
  }

  isSelected(item: SearchResult): boolean {
    return !!this.selectedSearchResults.find((i: any) => i.id === item.id)
  }

  onSearchInputKeyup(event?: any): void {
    if (this.readonly) return

    if (event) {
      this.terms = event.target.value

      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
        return this.navigateSuggestedValues(event.key)
      }
    }

    if (!this.resources?.length) return

    this.showList = true
    this.searchComplete = false
    this.searching = true
    this.search$.next(this.terms)
  }

  getCatalogueUrl(item: SearchResult): string | null {
    if (!item.slug) return null
    const type = item.resourceName === 'Metric' ? 'metrics' : 'tools'
    return `https://oecd.ai/en/catalogue/${type}/${item.slug}`
  }

  getResourceIcon(resourceClassName: string): string {
    const resource = this.resources.find((r) => r.className === resourceClassName)
    return resource ? resource.icon : 'icon-grid'
  }

  private formatToEmit(selectedResults: SearchResult[]): { [key: string]: number[] } {
    const key = this.resources[0].className.charAt(0).toLowerCase() + this.resources[0].className.slice(1) + 'Ids'
    return { [key]: selectedResults.map((r) => r.id) }
  }

  private navigateSuggestedValues(key: string): void {
    if (key === 'ArrowDown') {
      if (typeof this.focusedItemIndex === 'undefined') {
        this.showList = true
        this.focusedItemIndex = 0
      } else if (this.focusedItemIndex < this.suggestedSearchResults.length - 1) {
        this.focusedItemIndex++
      }
    } else if (key === 'ArrowUp') {
      if (!this.focusedItemIndex) {
        this.showList = false
        delete this.focusedItemIndex
      } else {
        this.focusedItemIndex--
      }
    } else if (key === 'Enter' && typeof this.focusedItemIndex !== 'undefined' && this.suggestedSearchResults[this.focusedItemIndex]) {
      this.toggleItem(this.suggestedSearchResults[this.focusedItemIndex])
      delete this.focusedItemIndex
    }
  }

  clearSearch(): void {
    this.terms = ''
    this.renderer.setProperty(this.searchInputEl.nativeElement, 'value', '')
    this.showList = false
    this.searching = false
    this.suggestedSearchResults = []
  }

  openSuggestPage(): void {
    const type = this.resourceType === 'metric' ? 'metrics' : 'tools'
    window.open(`https://oecd.ai/en/catalogue/${type}/submit`, '_blank')
  }

  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget) {
    if (this.showList && !this.elementRef.nativeElement.contains(eventTarget)) {
      this.showList = false
    }
  }
}
